import { Injectable, OnDestroy, signal, computed } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { UnleashClient, IConfig } from 'unleash-proxy-client';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService implements OnDestroy {
  private _unleash!: UnleashClient;
  private readonly _ready$ = new BehaviorSubject<boolean>(false);
  private readonly _updateCounter = signal(0);

  private readonly _flagSignals = new Map<
    string,
    ReturnType<typeof computed<boolean>>
  >();

  constructor() {
    this.initializeUnleash();
  }

  private initializeUnleash() {
    this.clearUnleashStorage();

    const config: IConfig = {
      url: environment.aws.featureFlagEndpoint,
      clientKey: 'not-used',
      appName: environment.aws.featureFlagAppName,
      refreshInterval: 15,
      disableMetrics: true,
    };

    try {
      this._unleash = new UnleashClient(config);

      this._unleash.on('ready', () => {
        this._ready$.next(true);
        this._updateCounter.update((v) => v + 1);
      });

      this._unleash.on('update', () => {
        this._updateCounter.update((v) => v + 1);
      });

      this._unleash.on('error', (error: any) => {
        console.error('Feature flags error:', error);
      });

      // Skipped under Karma to avoid leaking network requests / polling timers
      // that keep the browser alive past the test run (see src/test.ts).
      const autoStartDisabled =
        (globalThis as Record<string, unknown>)[
          '__DISABLE_UNLEASH_AUTOSTART__'
        ] === true;
      if (!autoStartDisabled) {
        this._unleash.start();
      }
    } catch (error) {
      console.error('Failed to initialize feature flags:', error);
      this._ready$.next(false);
    }
  }

  private clearUnleashStorage() {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('unleash') || key.includes('repo'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.error('Error clearing Unleash storage:', error);
    }
  }

  isReady(): Observable<boolean> {
    return this._ready$.asObservable();
  }

  isEnabled(featureName: string): boolean {
    if (!this._ready$.value || !this._unleash) {
      return false;
    }

    try {
      const toggles = this._unleash.getAllToggles();
      const flagExists = toggles.some((toggle) => toggle.name === featureName);
      if (!flagExists) {
        return false;
      }
      return this._unleash.isEnabled(featureName);
    } catch (error) {
      console.error(`Error checking feature flag ${featureName}:`, error);
      return false;
    }
  }

  createFlagSignal(featureName: string) {
    if (this._flagSignals.has(featureName)) {
      return this._flagSignals.get(featureName)!;
    }

    const flagSignal = computed(() => {
      this._updateCounter();

      if (!this._ready$.value) {
        return true;
      }

      return this.isEnabled(featureName);
    });

    this._flagSignals.set(featureName, flagSignal);
    return flagSignal;
  }

  async isEnabledWithContext(
    featureName: string,
    context: Record<string, string>
  ): Promise<boolean> {
    if (!this._ready$.value || !this._unleash) {
      console.warn(
        `Feature flag "${featureName}" unresolved (Unleash not ready) — defaulting to false.`
      );
      return false;
    }

    try {
      // updateContext re-fetches toggles from the proxy and is async;
      // a synchronous isEnabled() call after it would evaluate against the previous context.
      await this._unleash.updateContext(context);
      const toggles = this._unleash.getAllToggles();
      if (!toggles.some((toggle) => toggle.name === featureName)) {
        return false;
      }
      return this._unleash.isEnabled(featureName);
    } catch (error) {
      console.warn(
        `Feature flag "${featureName}" evaluation error — defaulting to false.`,
        error
      );
      return false;
    }
  }

  getVariant(featureName: string) {
    if (!this._ready$.value || !this._unleash) {
      return null;
    }

    try {
      return this._unleash.getVariant(featureName);
    } catch (error) {
      console.error(`Error getting variant for ${featureName}:`, error);
      return null;
    }
  }

  destroy() {
    if (this._unleash) {
      this._unleash.stop();
    }
  }

  // Stops Unleash's background polling when the injector is torn down.
  // Critical in Karma: without this each spec leaks a live client whose
  // refresh timer keeps Chrome busy, causing the post-run DISCONNECTED crash.
  ngOnDestroy() {
    this.destroy();
  }
}
